import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dashboard from "./Dashboard";
import EbookEmails from "./EbookEmails";
import EbooksManager from "./EbooksManager";
import PromptCategories from "./PromptCategories";
import Prompts from "./Prompts";
import PromptTags from "./PromptTags";
import SendWebinarEmails from "./SendWebinarEmails";
import WebinarsManager from "./WebinarsManager";
import AIAgentsPage from "./AIAgentsPage";
import EbookFeedbackManager from "./EbookFeedbackManager";
import BlogManager from "./BlogManager";
import BlogTopicSuggestions from "./BlogTopicSuggestions";
import styled, { css, keyframes } from "styled-components";

/* ========== THEME ========== */
const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  accent: "#ff914d",
  text: "#0f172a",
  subtext: "#d6d9de",
  bg: "#0b1320",
  card: "#0f1b2d",
  border: "#1e293b",
  hover: "rgba(255,255,255,0.06)",
  white: "#f1f1f1",
};

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* ========== LAYOUT ========== */
const Shell = styled.div`
  display: grid;
  grid-template-columns: ${({ $collapsed }) =>
    $collapsed ? "84px 1fr" : "260px 1fr"};
  grid-template-rows: 64px 1fr;
  grid-template-areas:
    "sidebar topbar"
    "sidebar main";
  min-height: 100vh;
  background: radial-gradient(
    1200px 800px at 10% -10%,
    #10233c 0%,
    #091120 50%,
    #060b14 100%
  );
  color: ${theme.white};
`;

const Sidebar = styled.aside`
  grid-area: sidebar;
  /* Stays pinned in its column and scrolls independently */
  position: sticky;
  top: 0;
  align-self: start;

  /* Make the sidebar its own scroll container */
  height: 100vh; /* full viewport */
  overflow-y: auto;
  overscroll-behavior: contain; /* don't bounce the whole page */
  -webkit-overflow-scrolling: touch; /* smooth iOS scrolling */

  background: linear-gradient(180deg, ${theme.card}, #0a1627 60%, #091220 100%);
  border-right: 1px solid ${theme.border};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 12px;

  /* Optional: custom scrollbar (webkit browsers) */
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${theme.brand} 0%, ${theme.brandDark} 100%);
  box-shadow: 0 10px 24px rgba(61, 114, 148, 0.35);
  user-select: none;
`;

const BrandLogo = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 2l8.66 5v10L12 22 3.34 17V7L12 2z" fill="white" opacity=".15" />
    <path d="M12 6l5.2 3v6L12 18l-5.2-3V9L12 6z" fill="white" />
  </svg>
);

const BrandName = styled.span`
  font-weight: 700;
  letter-spacing: 0.3px;
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      display: none;
    `}
`;

const CollapseBtn = styled.button`
  margin-left: auto;
  border: 0;
  background: ${theme.white};
  color: ${theme.text};
  font-weight: 700;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.2s ease;
  &:hover {
    transform: scale(1.04);
  }
`;

const NavGroup = styled.div`
  margin-top: 6px;
  display: grid;
  gap: 6px;
`;

const NavLabel = styled.div`
  font-size: 12px;
  letter-spacing: 0.6px;
  color: ${theme.subtext};
  padding: 8px ${({ $collapsed }) => ($collapsed ? "8px" : "12px")};
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      text-align: center;
    `}
`;

const Item = styled.button`
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: 0;
  cursor: pointer;
  padding: 10px ${({ $collapsed }) => ($collapsed ? "8px" : "12px")};
  background: transparent;
  color: ${theme.white};
  border-radius: 12px;
  transition: background 0.15s ease, transform 0.04s ease;
  &:hover {
    background: ${theme.hover};
  }
  ${({ $active }) =>
    $active &&
    css`
      background: linear-gradient(
        135deg,
        rgba(61, 114, 148, 0.2),
        rgba(42, 90, 122, 0.2)
      );
      outline: 1px solid rgba(61, 114, 148, 0.35);
    `}
`;

const IconWrap = styled.span`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
`;

const ItemText = styled.span`
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      display: none;
    `}
  text-align: left;
  font-size: 14px;
`;

const Badge = styled.span`
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      display: none;
    `}
  background: ${theme.accent};
  color: #1b1b1b;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 700;
`;

/* ========== TOPBAR ========== */
const Topbar = styled.header`
  grid-area: topbar;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid ${theme.border};
  background: rgba(10, 20, 36, 0.6);
  backdrop-filter: blur(8px);
  position: sticky;
  top: 0;
  z-index: 5;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(16px, 2.2vw, 20px);
  font-weight: 800;
  letter-spacing: 0.4px;
  color: ${theme.white};
  display: flex;
  align-items: center;
  gap: 10px;
  &:before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 22px;
    border-radius: 6px;
    background: ${theme.accent};
  }
`;

const RightCluster = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserChip = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 14px;
  padding: 6px 8px 6px 6px;
  cursor: pointer;
  position: relative;
`;

const Avatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.accent}, #ffc59f);
  color: #1e1e1e;
  font-weight: 800;
  display: grid;
  place-items: center;
  letter-spacing: 0.5px;
`;

const AdminName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.white};
`;

const Dropdown = styled.div`
  position: absolute;
  top: 48px;
  right: 0;
  min-width: 220px;
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  animation: pop 0.12s ease;
  @keyframes pop {
    from {
      transform: translateY(-4px);
      opacity: 0.3;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: transparent;
  border: 0;
  color: ${theme.white};
  cursor: pointer;
  font-size: 14px;
  text-align: left;
  &:hover {
    background: ${theme.hover};
  }
  &:not(:last-child) {
    border-bottom: 1px dashed ${theme.border};
  }
`;

/* ========== MAIN CONTENT ========== */
const Main = styled.main`
  grid-area: main;
  padding: 18px;
`;

/* ========== LOADER (auth check) ========== */
const fade = keyframes`
  0% { opacity: .3; }
  50% { opacity: 1; }
  100% { opacity: .3; }
`;
const Gate = styled.div`
  min-height: 100vh;
  background: radial-gradient(
    1200px 800px at 10% -10%,
    #10233c 0%,
    #091120 50%,
    #060b14 100%
  );
  display: grid;
  place-items: center;
  color: ${theme.white};
`;
const GateInner = styled.div`
  text-align: center;
  opacity: 0.9;
  animation: ${fade} 1.2s ease-in-out infinite;
`;

/* ========== ICONS ========== */
const Icon = ({ name }) => {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (name) {
    case "dashboard":
      return (
        <svg {...common}>
          <path d="M3 13h8V3H3zM13 21h8v-8h-8zM13 3h8v6h-8zM3 21h8v-6H3z" />
        </svg>
      );
    case "prompttags":
      return (
        <svg {...common}>
          <path d="M20.59 13.41L11 3.83c-.37-.37-.88-.58-1.41-.58H4c-1.1 0-2 .9-2 2v5.59c0 .53.21 1.04.59 1.41l9.59 9.59c.78.78 2.05.78 2.83 0l6.59-6.59c.78-.78.78-2.05 0-2.83zM6.5 8C5.67 8 5 7.33 5 6.5S5.67 5 6.5 5 8 5.67 8 6.5 7.33 8 6.5 8z" />
        </svg>
      );
    case "swebemails":
      return (
        <svg {...common}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v8h-2V6H4v12h10v2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zm16 10v4l4-4-4-4v3h-6v2h6z" />
        </svg>
      );
    case "webinars":
      return (
        <svg {...common}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zm0 2v12h16V6H4zm8 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm-6 8h2v2H6v-2zm12 0h2v2h-2v-2z" />
        </svg>
      );
    case "promptC":
      return (
        <svg {...common}>
          <path d="M4 4H11V11H4z M13 4H20V11H13z M4 13H11V20H4z M13 13H20V20H13z" />
        </svg>
      );
    case "prompts":
      return (
        <svg {...common}>
          <path d="M4 6h12v8H10l-3 3v-3H4z M6 9h8 M6 12h6" />
        </svg>
      );
    case "ebookEmails":
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="3" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "ebooks":
      return (
        <svg {...common}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M4 4.5A2.5 2.5 0 0 1 6.5 7H20v13H6.5A2.5 2.5 0 0 0 4 22V4.5z" />
          <path d="M6.5 7V22" />
        </svg>
      );
    case "aiagentsblogs":
      return (
        <svg {...common}>
          {/* Brain / AI nodes */}
          <circle cx="8" cy="8" r="2" />
          <circle cx="16" cy="8" r="2" />
          <circle cx="12" cy="12" r="2" />
          <path d="M8 8 L12 12 L16 8" />
          {/* Blog sheet / document shape */}
          <rect x="4" y="14" width="16" height="6" rx="1" />
          <path d="M6 16h8M6 18h5" />
        </svg>
      );
    case "ebookFeedbacks": // book + chat bubble
      return (
        <svg {...common}>
          {/* Book */}
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M4 4.5A2.5 2.5 0 0 1 6.5 7H20v13H6.5A2.5 2.5 0 0 0 4 22V4.5z" />
          <path d="M6.5 7V22" />
          {/* Chat bubble */}
          <path d="M13 5.5c0-1.38 1.34-2.5 3-2.5h4c1.66 0 3 1.12 3 2.5v3c0 1.38-1.34 2.5-3 2.5h-1.2l-1.7 1.7a.8.8 0 0 1-1.1-1.1l.8-.8H16c-1.66 0-3-1.12-3-2.5v-3z" />
          <path d="M16.5 6.75h.01" />
          <path d="M18.8 6.75h.01" />
        </svg>
      );
    case "blog":
      return (
        <svg {...common}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M4 4.5A2.5 2.5 0 0 1 6.5 7H20v13H6.5A2.5 2.5 0 0 0 4 22V4.5z" />
          <path d="M8 8h8M8 11h8M8 14h5" />
        </svg>
      );
    case "topics":
      return (
        <svg {...common}>
          <path d="M9 18h6" />
          <path d="M10 22h4" />
          <path d="M12 2a6 6 0 0 0-4 10.47c.53.48.9 1.09 1.03 1.78L9.3 16h5.4l.27-1.75c.13-.69.5-1.3 1.03-1.78A6 6 0 0 0 12 2z" />
        </svg>
      );
    case "settings":
      return (
        <svg {...common}>
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 0-.33 1.82v.08a2 2 0 1 1-4 0v-.08A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.6-1 1.65 1.65 0 0 0-1.82-.33h-.08a2 2 0 1 1 0-4h.08A1.65 1.65 0 0 0 4 9a1.65 1.65 0 0 0 .6-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6 1.65 1.65 0 0 0 .33-1.82v-.08a2 2 0 1 1 4 0v.08A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1 .6 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06c-.48.48-.62 1.18-.33 1.82.27.6.86.99 1.52.99H21a2 2 0 1 1 0 4h-.09c-.66 0-1.25.39-1.52.99z" />
        </svg>
      );
    case "logout":
      return (
        <svg {...common}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="M16 17l5-5-5-5" />
          <path d="M21 12H9" />
        </svg>
      );
    case "account":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20a8 8 0 0 1 16 0" />
        </svg>
      );
    default:
      return null;
  }
};

/* ========== HOOKS ========== */
function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler();
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

/* ========== COMPONENT ========== */
export default function AdminLayout() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [active, setActive] = useState("Dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Auth gate state
  const [checkingAuth, setCheckingAuth] = useState(true);

  const admin = useMemo(() => ({ name: "Abhishek Shete", initials: "AS" }), []);

  const menuRef = useRef(null);
  useOnClickOutside(menuRef, () => setMenuOpen(false));

  const nav = [
    { key: "Dashboard", icon: "dashboard" },
    { key: "Ebooks Manager", icon: "ebooks" },
    { key: "Ebook Emails", icon: "ebookEmails" },
    { key: "Prompt Categories", icon: "promptC" },
    { key: "Prompt Tags", icon: "prompttags" },
    { key: "Construction Prompts", icon: "prompts" },
    { key: "Webinars Manager", icon: "webinars" },
    { key: "Send Webinar Emails", icon: "swebemails" },
    { key: "AI Agents Manager", icon: "aiagentsblogs" },
    { key: "Blog Manager", icon: "blog" },
    { key: "Blog Topic Suggestions", icon: "topics" },
    { key: "Ebook Feedbacks Manager", icon: "ebookFeedbacks" }, // icon + fixed label
  ];

  // ===== Auth Guard: if not logged in, redirect to /admin/login
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          method: "GET",
          credentials: "include",
        });

        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          if (!ignore) {
            setCheckingAuth(false);
            navigate("/admin/login", { replace: true });
          }
          return;
        }

        const data = await res.json().catch(() => null);
        const ok = res.ok && data && data.admin;
        if (!ignore) {
          if (!ok) {
            setCheckingAuth(false);
            navigate("/admin/login", { replace: true });
          } else {
            setCheckingAuth(false);
          }
        }
      } catch {
        if (!ignore) {
          setCheckingAuth(false);
          navigate("/admin/login", { replace: true });
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [navigate]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setMenuOpen(false);

    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      navigate("/admin/login", { replace: true });
    } catch {
      navigate("/admin/login", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  // While checking auth, show a lightweight gate
  if (checkingAuth) {
    return (
      <Gate>
        <GateInner>Checking access…</GateInner>
      </Gate>
    );
  }

  return (
    <Shell $collapsed={collapsed}>
      {/* SIDEBAR */}
      <Sidebar>
        <Brand>
          <BrandLogo />
          <BrandName $collapsed={collapsed}>Admin Panel</BrandName>
          <CollapseBtn
            title={collapsed ? "Expand" : "Collapse"}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? "»" : "«"}
          </CollapseBtn>
        </Brand>

        <NavLabel $collapsed={collapsed}>OVERVIEW</NavLabel>
        <NavGroup>
          {nav.map((n) => (
            <Item
              key={n.key}
              $collapsed={collapsed}
              $active={active === n.key}
              onClick={() => setActive(n.key)}
            >
              <IconWrap>
                <Icon name={n.icon} />
              </IconWrap>
              <ItemText $collapsed={collapsed}>{n.key}</ItemText>
              {n.badge ? (
                <Badge $collapsed={collapsed}>{n.badge}</Badge>
              ) : (
                <span />
              )}
            </Item>
          ))}
        </NavGroup>
      </Sidebar>

      {/* TOPBAR */}
      <Topbar>
        <Title>AI Building Skills ADMIN PANEL</Title>

        <RightCluster>
          <UserChip onClick={() => setMenuOpen((o) => !o)} ref={menuRef}>
            <Avatar>{admin.initials}</Avatar>
            <AdminName>{admin.name}</AdminName>

            {menuOpen && (
              <Dropdown>
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    alert("Open Account Settings");
                  }}
                >
                  <Icon name="account" />
                  Account Settings
                </MenuItem>
                <MenuItem onClick={handleLogout} disabled={loggingOut}>
                  <Icon name="logout" />
                  {loggingOut ? "Logging out..." : "Logout"}
                </MenuItem>
              </Dropdown>
            )}
          </UserChip>
        </RightCluster>
      </Topbar>

      {/* MAIN CONTENT */}
      <Main>
        {active === "Dashboard" && <Dashboard />}
        {active === "Ebooks Manager" && <EbooksManager />}
        {active === "Ebook Emails" && <EbookEmails />}
        {active === "Prompt Categories" && <PromptCategories />}
        {active === "Prompt Tags" && <PromptTags />}
        {active === "Construction Prompts" && <Prompts />}
        {active === "Webinars Manager" && <WebinarsManager />}
        {active === "Send Webinar Emails" && <SendWebinarEmails />}
        {active === "AI Agents Manager" && <AIAgentsPage />}
        {active === "Blog Manager" && <BlogManager />}
        {active === "Blog Topic Suggestions" && <BlogTopicSuggestions />}
        {active === "Ebook Feedbacks Manager" && <EbookFeedbackManager />}
      </Main>
    </Shell>
  );
}
